import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { BANKING_PERMISSIONS } from "@folio/shared/permissions";
import { useTenantStore } from "@/stores/tenant-store";
import { Button } from "@/components/ui/button";
import { bankingApi } from "@/features/banking/api";
import { BankingWorkspace } from "@/features/banking/components/BankingWorkspace";
import { BankAccounts } from "@/features/banking/components/BankAccounts";
import { BankingImportDetail } from "@/features/banking/components/BankingImportDetail";
import {
  BankingBoundary,
  BankingError,
} from "@/features/banking/components/BankingShared";
import { useBankingRequest } from "@/features/banking/hooks/useBankingRequest";
import type { BankingBatchDetailDto } from "@/features/banking/types";

export function BankingPage() {
  const tenant = useTenantStore((s) => s.activeTenantId);
  const organization = useTenantStore((s) => s.activeOrganizationId);
  const scope = `${tenant}:${organization}`;
  const [selectionScope, setSelectionScope] = useState(scope);
  const [, setParams] = useSearchParams();
  useEffect(() => {
    if (scope !== selectionScope) {
      setParams({}, { replace: true });
      setSelectionScope(scope);
    }
  }, [scope, selectionScope, setParams]);
  return (
    <BankingBoundary permission={BANKING_PERMISSIONS.importRead}>
      <h1 className="text-2xl font-semibold">Bancos</h1>
      {scope === selectionScope && <BankingWorkspace />}
    </BankingBoundary>
  );
}
export function BankAccountsPage() {
  return (
    <BankingBoundary permission={BANKING_PERMISSIONS.accountRead}>
      <h1 className="text-2xl font-semibold">
        Configuración de cuentas bancarias
      </h1>
      <BankAccounts />
    </BankingBoundary>
  );
}
export function BankingDetailPage() {
  const { batchId = "" } = useParams();
  const tenant = useTenantStore((s) => s.activeTenantId);
  const organization = useTenantStore((s) => s.activeOrganizationId);
  // A historical deep link may be opened in a selected scope, but an open detail
  // is invalidated when that scope changes. Never replay its ID in another scope.
  const [entryScope, setEntryScope] = useState<string | null>(
    tenant && organization ? `${tenant}:${organization}` : null,
  );
  useEffect(() => {
    if (!entryScope && tenant && organization)
      setEntryScope(`${tenant}:${organization}`);
  }, [entryScope, tenant, organization]);
  const sameScope = entryScope === `${tenant}:${organization}`;
  return (
    <BankingBoundary permission={BANKING_PERMISSIONS.importRead}>
      <Button variant="link" asChild>
        <Link to="/contabilidad/bancos">Volver a bancos</Link>
      </Button>
      {sameScope ? (
        <DetailLoader key={batchId} batchId={batchId} />
      ) : (
        <p role="alert">
          Cambió la organización o empresa. Vuelve a bancos para elegir una
          importación en el nuevo contexto.
        </p>
      )}
    </BankingBoundary>
  );
}
function DetailLoader({ batchId }: { batchId: string }) {
  const [batch, setBatch] = useState<BankingBatchDetailDto | null>(null);
  const request = useBankingRequest();
  const { run } = request;
  useEffect(() => {
    void run((signal) => bankingApi.detail(batchId, signal), setBatch);
  }, [run, batchId]);
  return (
    <>
      <BankingError error={request.error} />
      {request.pending && <p>Cargando importación…</p>}
      {request.error && (
        <Button
          variant="outline"
          onClick={() =>
            void run((signal) => bankingApi.detail(batchId, signal), setBatch)
          }
        >
          Reintentar carga
        </Button>
      )}
      {batch && (
        <BankingImportDetail
          key={`${batch.id}:${batch.previewRevision}`}
          initial={batch}
        />
      )}
    </>
  );
}
